
import React, { useState, useRef } from 'react';
import { MemberModel } from '../../../../models';
import { db, storage } from '../../../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../../services/imageOptimizer';
import { ChevronLeft, Send, Image as ImageIcon, X, Loader2, User, MessageSquare } from 'lucide-react';

interface StudentQAFormProps {
    member: MemberModel;
    onBack: () => void;
    onSave: () => void;
}

const StudentQAForm: React.FC<StudentQAFormProps> = ({ member, onBack, onSave }) => {
    const [question, setQuestion] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 업로드 가능합니다.');
            return;
        }
        setImage(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmitQuestion = async () => {
        if (!question.trim()) return alert('질문 내용을 입력해주세요.');

        setIsSubmitting(true);
        try {
            // 1. Upload Image if exists
            let imageUrl = '';
            if (image) {
                const storageRef = ref(storage, `memberQA/${member.id}/${Date.now()}_${image.name.replace(/\.[^/.]+$/, "")}.webp`);
                const snapshot = await uploadOptimizedImage(storageRef, image, { maxWidth: 1600, quality: 0.85 });
                imageUrl = await getDownloadURL(snapshot.ref);
            }

            // 2. Save to Firestore
            await addDoc(collection(db, 'studentQA'), {
                memberId: member.id,
                branchId: member.branchId,
                question,
                imageUrl,
                createdAt: Date.now()
            });

            alert('질문이 등록되었습니다.');
            onSave();
        } catch (error: any) {
            alert('질문 등록 실패: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-[1.5rem] md:rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <header className="px-4 py-4 md:px-8 md:py-6 border-b border-slate-50 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3 md:gap-4">
                    <button onClick={onBack} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-100 transition-all">
                        <ChevronLeft size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <MessageSquare size={16} />
                        </div>
                        <h3 className="text-lg md:text-xl font-black text-slate-800 tracking-tight">학습 질문하기</h3>
                    </div>
                </div>
            </header>

            <div
                className={`flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar transition-all relative ${isDragging ? 'bg-indigo-50/50' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isDragging && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none border-4 border-dashed border-indigo-200 rounded-[3rem] m-4 animate-in fade-in duration-200">
                        <div className="w-20 h-20 bg-indigo-600/10 text-indigo-600 rounded-full flex items-center justify-center mb-4">
                            <ImageIcon size={40} />
                        </div>
                        <p className="text-lg font-black text-indigo-600">여기에 사진을 놓으세요!</p>
                    </div>
                )}
                <div className="max-w-3xl mx-auto space-y-6 md:space-y-8">
                    {/* Question Input Section */}
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 pl-1">
                                <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                                    <User size={14} />
                                </div>
                                <span className="text-xs font-bold text-slate-500">나의 질문</span>
                            </div>
                            <div className="space-y-3">
                                <textarea
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder="질문 내용을 입력하세요. 문제 사진을 함께 첨부할 수 있습니다."
                                    className="w-full min-h-[160px] bg-slate-50 border-none rounded-[1.5rem] p-5 text-sm font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all resize-none leading-relaxed"
                                />

                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0">
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-100 rounded-xl text-[11px] font-black text-slate-500 hover:bg-slate-50 transition-all"
                                        >
                                            <ImageIcon size={14} className="text-indigo-500" />
                                            사진 첨부
                                        </button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleImageChange}
                                            accept="image/*"
                                            className="hidden"
                                        />
                                    </div>

                                    <button
                                        onClick={handleSubmitQuestion}
                                        disabled={isSubmitting || !question.trim()}
                                        className="flex items-center justify-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-[11px] font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50"
                                    >
                                        {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                        질문 등록하기
                                    </button>
                                </div>

                                {previewUrl && (
                                    <div className="relative w-full max-w-sm rounded-2xl overflow-hidden border border-slate-100 shadow-sm animate-in zoom-in-95 duration-200">
                                        <img src={previewUrl} alt="Preview" className="w-full h-auto" />
                                        <button
                                            onClick={handleRemoveImage}
                                            className="absolute top-3 right-3 w-7 h-7 bg-black/50 text-white rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-black/70 transition-all"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentQAForm;
